import { useCallback, useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass } from "../components/FormField";
import { fetchSettlements, type Settlement } from "../api/claimsApi";
import { openSettlementSheet } from "../api/invoicePrintApi";

function money(value: number): string {
  return value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function previousMonth(): string {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - 1);
  return date.toISOString().slice(0, 7);
}

/**
 * Compte Sinistre : bordereaux mensuels (mémos de règlement) par prestataire,
 * à partir des factures contrôlées du mois.
 */
export function ClaimsSettlementsPage() {
  const [month, setMonth] = useState(previousMonth());
  const [rows, setRows] = useState<Settlement[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (value: string) => {
    try {
      setError(null);
      setRows(await fetchSettlements(value));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Chargement impossible.");
    }
  }, []);

  useEffect(() => {
    void load(month);
  }, [month, load]);

  const print = async (hospitalId: number) => {
    try {
      await openSettlementSheet(hospitalId, month);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impression impossible.");
    }
  };

  const total = rows.reduce((sum, row) => sum + row.to_pay, 0);

  return (
    <>
      <Breadcrumbs items={[{ label: "Bordereaux" }]} />
      <section className="theme-card p-5">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-foreground">Bordereaux mensuels</h1>
            <p className="text-sm text-muted">Un mémo de règlement par prestataire, à partir des factures marquées « contrôlées ».</p>
          </div>
          <FormField label="Mois">
            <input id="settlement-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={inputClass} />
          </FormField>
        </div>
        {error ? <p className="mb-3 text-sm text-red-600">{error}</p> : null}
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-card-muted text-left text-xs uppercase text-muted">
              <tr>
                <th className="px-3 py-2">Prestataire</th>
                <th className="px-3 py-2 text-right">Factures</th>
                <th className="px-3 py-2 text-right">Facturé</th>
                <th className="px-3 py-2 text-right">Rejeté</th>
                <th className="px-3 py-2 text-right">À payer</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-3 text-muted">Aucune facture contrôlée sur ce mois.</td>
                </tr>
              ) : null}
              {rows.map((row) => (
                <tr key={row.hospital_id} className="border-t border-border">
                  <td className="px-3 py-2 font-medium text-foreground">{row.hospital}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.invoices}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.billed)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.rejected)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{money(row.to_pay)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => void print(row.hospital_id)}
                      className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-accent/20 hover:border-accent"
                    >
                      <Printer size={14} />
                      Mémo de règlement
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 ? (
              <tfoot>
                <tr className="border-t border-border bg-card-muted font-semibold">
                  <td className="px-3 py-2" colSpan={4}>Total à payer sur le mois</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(total)}</td>
                  <td />
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </section>
    </>
  );
}
